// src/components/cofounder/ActionConfirmation.js
import React from 'react';
import { CheckCircle, XCircle, Loader, AlertTriangle } from 'lucide-react';
import { useCoFounder } from '../../context/CoFounderContext';

const ActionConfirmation = ({ action }) => {
  const { confirmAction, rejectAction } = useCoFounder();

  const statusConfig = {
    pending: { bg: 'bg-amber-50', border: 'border-amber-200', icon: AlertTriangle, iconColor: 'text-amber-500' },
    executing: { bg: 'bg-blue-50', border: 'border-blue-200', icon: Loader, iconColor: 'text-blue-500' },
    completed: { bg: 'bg-green-50', border: 'border-green-200', icon: CheckCircle, iconColor: 'text-green-500' },
    failed: { bg: 'bg-red-50', border: 'border-red-200', icon: XCircle, iconColor: 'text-red-500' },
    rejected: { bg: 'bg-gray-50', border: 'border-gray-200', icon: XCircle, iconColor: 'text-gray-400' }
  };

  const config = statusConfig[action.status] || statusConfig.pending;
  const StatusIcon = config.icon;

  const formatArgs = (args) => {
    if (!args) return [];
    return Object.entries(args).map(([key, value]) => ({
      key,
      value: typeof value === 'object' ? JSON.stringify(value) : String(value)
    }));
  };

  return (
    <div className={`rounded-lg border ${config.border} ${config.bg} p-3 my-2`}>
      <div className="flex items-start gap-2">
        <StatusIcon size={18} className={`${config.iconColor} mt-0.5 flex-shrink-0 ${action.status === 'executing' ? 'animate-spin' : ''}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm text-gray-800">{action.toolName}</span>
            <span className="text-xs text-gray-500 capitalize">({action.status})</span>
          </div>

          {action.toolDescription && (
            <p className="text-xs text-gray-600 mt-0.5">{action.toolDescription}</p>
          )}

          {action.args && Object.keys(action.args).length > 0 && (
            <div className="mt-2 space-y-1">
              {formatArgs(action.args).map(({ key, value }) => (
                <div key={key} className="flex gap-2 text-xs">
                  <span className="text-gray-500 font-medium min-w-0">{key}:</span>
                  <span className="text-gray-700 truncate">{value}</span>
                </div>
              ))}
            </div>
          )}

          {action.status === 'pending' && (
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => confirmAction(action.id)}
                className="px-3 py-1.5 bg-[#190192] text-white text-xs font-medium rounded-lg hover:bg-[#0f0075] transition-colors"
              >
                Confirm
              </button>
              <button
                onClick={() => rejectAction(action.id)}
                className="px-3 py-1.5 bg-white text-gray-700 text-xs font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          )}

          {action.status === 'failed' && action.error && (
            <p className="text-xs text-red-600 mt-1">Error: {action.error}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActionConfirmation;
